import type { InventoryCategory, InventoryItem, InventoryUnit } from '../types/inventory'

import { ensureOfflineReady } from '../offline/initOffline'
import { enqueueEntityChange } from '../offline/outbox/entityOutbox'
import { dexieInventoryItemRepo } from '../offline/repos/DexieInventoryItemRepo'
import { api } from './api'
import {
  buildQueuedEntityMutationResult,
  readEntityBatchSyncStatus,
  retryEntityBatchSyncStatusWithReport,
  syncedEntityMutationResult,
  type EntityBatchSyncRetryReport,
  type EntityBatchSyncStatus,
  type EntityMutationResult
} from './entityMutationSync'
import { createId } from './id'
import { saveInventoryItems } from './inventoryStorage'

const nowIso = (): string => new Date().toISOString()

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T

const sortByName = (items: InventoryItem[]): InventoryItem[] => items.slice().sort((a, b) => a.name.localeCompare(b.name))

const writeLocalMirrorBestEffort = async (): Promise<void> => {
  try {
    const items = await dexieInventoryItemRepo.list()
    saveInventoryItems(items)
  } catch {
    // ignore
  }
}

const toNumber = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string') {
    const trimmed = value.trim().replace(',', '.')
    if (!trimmed) return null
    const parsed = Number(trimmed)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

const toOptionalString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed ? trimmed : undefined
}

const isPlainObject = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
)

const toOptionalIsoDate = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  if (!trimmed) return undefined
  // Date-only strings (YYYY-MM-DD)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return undefined
  return trimmed
}

const ensureWarehouseDates = (item: InventoryItem): InventoryItem => {
  const createdFallback = typeof item.createdAt === 'string' && item.createdAt.length >= 10
    ? item.createdAt.slice(0, 10)
    : undefined

  const baseMeta = isPlainObject(item.metadata) ? item.metadata : {}
  const receivedOn = toOptionalIsoDate((baseMeta as any).receivedOn) ?? createdFallback
  const expiresOn = toOptionalIsoDate((baseMeta as any).expiresOn)

  // If we still don't have receivedOn, leave metadata alone; UI will require it on edit/save.
  if (!receivedOn && !expiresOn && Object.keys(baseMeta).length === 0) return item

  const nextMeta: Record<string, unknown> = { ...baseMeta }
  if (receivedOn) nextMeta.receivedOn = receivedOn
  if (expiresOn) nextMeta.expiresOn = expiresOn

  return { ...item, metadata: nextMeta }
}

const normalizeCategory = (value: unknown): InventoryCategory => {
  if (typeof value !== 'string') return 'other'
  switch (value.trim().toLowerCase()) {
    case 'feed':
      return 'feed'
    case 'chemical':
      return 'chemical'
    case 'equipment':
      return 'equipment'
    default:
      return 'other'
  }
}

const normalizeUnit = (value: unknown): InventoryUnit => {
  if (typeof value !== 'string') return 'pcs'
  switch (value.trim().toLowerCase()) {
    case 'kg':
      return 'kg'
    case 'l':
      return 'l'
    case 'pcs':
      return 'pcs'
    default:
      return 'pcs'
  }
}

const normalizeRemoteItem = (candidate: any): InventoryItem | null => {
  if (!candidate || typeof candidate !== 'object') return null
  const id = typeof candidate.id === 'string' ? candidate.id.trim() : ''
  const farmId = typeof candidate.farmId === 'string' ? candidate.farmId.trim() : ''
  const name = typeof candidate.name === 'string' ? candidate.name.trim() : ''
  if (!id || !farmId || !name) return null

  const onHand = toNumber(candidate.onHand)
  if (onHand === null || onHand < 0) return null

  const reorderPoint = toNumber(candidate.reorderPoint)

  const createdAt = typeof candidate.createdAt === 'string' ? candidate.createdAt : nowIso()
  const updatedAt = typeof candidate.updatedAt === 'string' ? candidate.updatedAt : createdAt

  const metadata = isPlainObject(candidate.metadata) ? candidate.metadata : undefined
  const rawUnitCostCents = toNumber(candidate.unitCostCents)
  const unitCostCents =
    rawUnitCostCents !== null && Number.isInteger(rawUnitCostCents) && rawUnitCostCents >= 0
      ? rawUnitCostCents
      : undefined
  const currency = toOptionalString(candidate.currency)
  return ensureWarehouseDates({
    id,
    farmId,
    name,
    category: normalizeCategory(candidate.category),
    unit: normalizeUnit(candidate.unit),
    onHand,
    reorderPoint: reorderPoint !== null && reorderPoint >= 0 ? reorderPoint : undefined,
    usageFeedType: toOptionalString(candidate.usageFeedType),
    unitCostCents,
    currency,
    notes: toOptionalString(candidate.notes),
    metadata,
    createdAt,
    updatedAt
  })
}

export type InventoryMutationResult = EntityMutationResult<InventoryItem[]>

export const inventoryApi = {
  readInventoryItemSyncStatus: async (itemIds: string[]): Promise<EntityBatchSyncStatus> => {
    return readEntityBatchSyncStatus('inventoryItem', itemIds)
  },

  retryInventoryItemSyncStatusWithReport: async (itemIds: string[]): Promise<EntityBatchSyncRetryReport> => {
    return retryEntityBatchSyncStatusWithReport({
      entityType: 'inventoryItem',
      entityIds: itemIds
    })
  },

  async listAll(): Promise<InventoryItem[]> {
    await ensureOfflineReady()

    try {
      const remote = await api.inventoryItems.list({ noCache: true })
      const mapped = remote
        .map(normalizeRemoteItem)
        .filter((item): item is InventoryItem => Boolean(item))

      const sorted = sortByName(mapped)
      await dexieInventoryItemRepo.replaceAll(sorted)
      void writeLocalMirrorBestEffort()
      return clone(sorted)
    } catch (error) {
      const cached = await dexieInventoryItemRepo.list()
      if (cached.length > 0) {
        const normalizedCached = cached.map(ensureWarehouseDates)
        // Best effort: backfill metadata locally so "received on" becomes available even offline.
        try {
          await dexieInventoryItemRepo.upsertMany(normalizedCached)
        } catch {
          // ignore
        }
        return clone(sortByName(normalizedCached))
      }
      // Last resort: legacy mirror (should be migrated during offline init).
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const { loadInventoryItems } = await import('./inventoryStorage')
      return clone(sortByName(loadInventoryItems().map(ensureWarehouseDates)))
    }
  },

  async upsert(item: InventoryItem): Promise<InventoryMutationResult> {
    await ensureOfflineReady()
    await dexieInventoryItemRepo.upsert(item)

    try {
      await enqueueEntityChange({
        entityType: 'inventoryItem',
        op: 'upsert',
        entityId: item.id,
        payload: item
      })
    } catch {
      // best-effort
    }

    void writeLocalMirrorBestEffort()

    const next = await dexieInventoryItemRepo.list()
    return buildQueuedEntityMutationResult(clone(sortByName(next)), {
      entityType: 'inventoryItem',
      entityId: item.id
    })
  },

  async create(args: {
    farmId: string
    name: string
    category: InventoryCategory
    unit: InventoryUnit
    onHand: number
    reorderPoint?: number
    usageFeedType?: string
    unitCostCents?: number | null
    currency?: string | null
    notes?: string
    receivedOn: string
    expiresOn?: string
  }): Promise<InventoryMutationResult> {
    const timestamp = nowIso()
    const baseMeta = {
      receivedOn: args.receivedOn,
      ...(args.expiresOn ? { expiresOn: args.expiresOn } : null)
    }
    const item: InventoryItem = {
      id: createId('inv'),
      farmId: args.farmId.trim(),
      name: args.name.trim(),
      category: args.category,
      unit: args.unit,
      onHand: args.onHand,
      reorderPoint: args.reorderPoint,
      usageFeedType: args.usageFeedType?.trim() || undefined,
      unitCostCents:
        typeof args.unitCostCents === 'number' && Number.isInteger(args.unitCostCents) && args.unitCostCents >= 0
          ? args.unitCostCents
          : undefined,
      currency: args.currency?.trim() ? args.currency.trim().toUpperCase() : undefined,
      notes: args.notes?.trim() || undefined,
      metadata: baseMeta,
      createdAt: timestamp,
      updatedAt: timestamp
    }

    return this.upsert(ensureWarehouseDates(item))
  },

  async delete(id: string): Promise<InventoryMutationResult> {
    await ensureOfflineReady()
    const trimmed = id.trim()
    if (!trimmed) {
      const current = await this.listAll()
      return syncedEntityMutationResult(current)
    }

    await dexieInventoryItemRepo.delete(trimmed)

    try {
      await enqueueEntityChange({
        entityType: 'inventoryItem',
        op: 'delete',
        entityId: trimmed
      })
    } catch {
      // best-effort
    }

    void writeLocalMirrorBestEffort()

    const next = await dexieInventoryItemRepo.list()
    return buildQueuedEntityMutationResult(clone(sortByName(next)), {
      entityType: 'inventoryItem',
      entityId: trimmed
    })
  }
}
